// ─────────────────────────────────────────────
// Télémétrie plateforme PWA (ADR 0007 D2 — Métriques A/B du cap natif).
//
// Données DATÉES, JAMAIS une décision métier : ce module ne fait QUE
// observer l'état device (standalone/notifs/plateforme) pour alimenter le
// tableau de bord admin — aucune branche produit ne doit jamais lire ce
// module pour gater une fonctionnalité. Voir docs/adr/0007-cap-pwa-natif.md.
//
// Wrap-ready (ADR 0007 D3) : détection purement feature-testée (guards sur
// `'Notification' in window`, `matchMedia`, `navigator.standalone`) — aucune
// dépendance dure qui casserait dans un WebView Capacitor.
// ─────────────────────────────────────────────

export type PwaPlatform = 'ios' | 'android' | 'other';
export type PwaNotifPermission = 'granted' | 'denied' | 'default';

export interface PwaTelemetryPayload {
  standalone: boolean;
  notifPermission: PwaNotifPermission;
  platform: PwaPlatform;
}

/**
 * Détection plateforme par UA — iPhone/iPad/iPod → ios, Android → android,
 * sinon other.
 *
 * iPad moderne (revue tâche 1) : depuis iPadOS 13, Safari envoie un UA
 * "Macintosh" indiscernable d'un vrai Mac (Apple a retiré "iPad" de l'UA par
 * défaut pour forcer le rendu desktop). Le seul signal fiable qui distingue
 * un iPad de ce Mac usurpé est `navigator.maxTouchPoints > 1` (un vrai Mac à
 * trackpad/souris ne l'a pas ; un iPad tactile si) — cf. la recommandation
 * WebKit officielle pour cette détection.
 */
export function detectPlatform(): PwaPlatform {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent ?? '' : '';
  const maxTouchPoints = typeof navigator !== 'undefined' ? navigator.maxTouchPoints ?? 0 : 0;
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && maxTouchPoints > 1)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

/**
 * PWA installée (écran d'accueil) — `display-mode: standalone` (Android/
 * desktop) OU `navigator.standalone` (iOS/Safari, propriété non standard,
 * absente du type `Navigator` — d'où le cast local). Les deux guards sont
 * indépendants : un navigateur sans `matchMedia` (improbable, mais feature-
 * testé par prudence) ne doit pas faire planter la collecte.
 */
export function detectStandalone(): boolean {
  const mqStandalone =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(display-mode: standalone)').matches
      : false;
  const iosStandalone =
    typeof navigator !== 'undefined' &&
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return Boolean(mqStandalone || iosStandalone);
}

/**
 * État natif `Notification.permission` — 'default' si l'API est absente
 * (navigateur sans support push, ou contexte non-sécurisé) plutôt qu'une
 * erreur : c'est un état honnête ("pas demandé"), pas un échec de collecte.
 */
function detectNotifPermission(): PwaNotifPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'default';
  return Notification.permission;
}

/** Instantané synchrone de l'état device courant — aucun effet de bord. */
export function collectPwaTelemetry(): PwaTelemetryPayload {
  return {
    standalone: detectStandalone(),
    notifPermission: detectNotifPermission(),
    platform: detectPlatform(),
  };
}

// ─────────────────────────────────────────────
// Capture `beforeinstallprompt` (chantier 7 T2, ADR 0007 D1 — incitation
// forte à l'installation, seule voie du push sur iPhone). Émis par
// Chrome/Edge Android et desktop UNIQUEMENT — jamais par Safari iOS, qui n'a
// pas de prompt natif (d'où le tutoriel manuel §5 du kit pour ce segment).
//
// Écoute au NIVEAU MODULE, pas dans un composant : l'event ne peut être
// capturé qu'une fois, potentiellement avant que `InstallTutorial` ne soit
// jamais monté — le stocker ici le rend disponible quel que soit le moment
// où l'utilisateur ouvre le tutoriel depuis le Profil ou le nudge
// post-première-commande. `preventDefault()` supprime le mini-infobar natif
// de Chrome au profit du CTA maison ("Installer maintenant").
// ─────────────────────────────────────────────
export interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredInstallPrompt = event as BeforeInstallPromptEvent;
  });
}

/** Snapshot synchrone : un `beforeinstallprompt` a-t-il été capturé ? */
export function hasInstallPrompt(): boolean {
  return deferredInstallPrompt !== null;
}

/**
 * Déclenche le prompt natif capturé par le listener module-level ci-dessus.
 * L'event `beforeinstallprompt` est à usage unique (le navigateur ne le
 * réémet pas après un `.prompt()`) : on le consomme ici — `deferredInstallPrompt`
 * remis à `null` — quel que soit le choix de l'utilisateur ou une erreur,
 * jamais laissé dans un état "réutilisable" une seconde fois. Renvoie `true`
 * uniquement si l'utilisateur a accepté.
 */
export async function triggerInstallPrompt(): Promise<boolean> {
  const evt = deferredInstallPrompt;
  if (!evt) return false;
  deferredInstallPrompt = null;
  try {
    await evt.prompt();
    const choice = await evt.userChoice;
    return choice.outcome === 'accepted';
  } catch {
    return false;
  }
}

/**
 * TEST-ONLY. Remet `deferredInstallPrompt` à `null` — l'état est une
 * variable de module qui survit d'un test à l'autre dans le même fichier
 * (revue tâche 2 chantier 7 : fuite reproduite au shuffle, un test qui
 * dispatche `beforeinstallprompt` sans le consommer contaminait le suivant).
 * N'a aucun appelant en production ; câblée dans `resetPwaStubs()`
 * (test/pwaStubs.ts) pour que tout fichier qui l'utilise soit couvert sans
 * avoir à y penser à chaque suite.
 */
export function resetInstallPromptForTests(): void {
  deferredInstallPrompt = null;
}

const THROTTLE_KEY = 'myfood:pwa-telemetry-at';
const THROTTLE_MS = 24 * 60 * 60 * 1000;

/**
 * `localStorage` peut lever (mode privé strict, quota, contexte non-DOM en
 * test) — jamais laissé remonter : la télémétrie est un aparté, pas un
 * chemin critique du boot.
 */
function readLastSentAt(): number | null {
  try {
    const raw = localStorage.getItem(THROTTLE_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function markSent(now: number): void {
  try {
    localStorage.setItem(THROTTLE_KEY, String(now));
  } catch {
    /* best-effort — un throttle raté entraîne au pire un envoi de plus */
  }
}

/**
 * Collecte + envoie la télémétrie PWA si le throttle 24h est dépassé, via le
 * `send` fourni par l'appelant (découplé de l'implémentation HTTP — testable
 * sans réseau). Ne marque l'envoi comme fait QUE sur succès : un échec réseau
 * ponctuel (offline, boot avant connexion) laisse une chance au prochain
 * boot de collecter la baseline, sans jamais relancer plus d'une fois par
 * montage. Échec TOUJOURS silencieux (jamais de throw) — appelée au boot,
 * elle ne doit jamais casser le rendu de l'app (ADR 0007 D2).
 */
export async function sendPwaTelemetryIfDue(
  send: (payload: PwaTelemetryPayload) => Promise<unknown>,
  now: number = Date.now(),
): Promise<void> {
  const lastSentAt = readLastSentAt();
  if (lastSentAt !== null && now - lastSentAt < THROTTLE_MS) return;

  try {
    await send(collectPwaTelemetry());
    markSent(now);
  } catch {
    /* silencieux — cf. doc du module */
  }
}
